import { useCallback, useEffect, useMemo, useState } from 'react';

import { organizationsApi, settingsApi, versionsApi } from '../../api';
import { Badge, Button, ErrorState, Loading, Select, Switch } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { useRuntimeSettings } from '../../context/RuntimeSettingsContext';
import { useToast } from '../../context/ToastContext';
import { relativeTime } from '../../utils/format';
import '../pages.css';

/**
 * Settings → General: the limits and defaults that used to be fixed in .env or code.
 *
 * Three layers, the most specific winning: the .env value (shown as "Default"), a
 * deployment value a superadmin saves, and — for the three conversation settings — an
 * override an organization's administrator saves for that organization. Everyone can
 * read the page; the API enforces who may change what, and the disabled controls here
 * are a courtesy.
 */

const APPLIES = {
  vllm: 'Has no effect on this deployment: it only applies with vLLM serving (INFERENCE_BACKEND=vllm).',
  queue:
    'Has no effect on this deployment: it only applies when the runner executes batch jobs (BATCH_EXECUTOR=queue).',
};

// ------------------------------------------------------------ value helpers
function toInput(row, value) {
  if (row.type === 'bool') return Boolean(value);
  if (row.type === 'list') return (value || []).join('\n');
  if (value === null || value === undefined) return '';
  return String(value);
}

function fromInput(row, input) {
  if (row.type === 'bool') return Boolean(input);
  if (row.type === 'list') {
    return input
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
  }
  if (row.type === 'version') return input.trim();
  return Number(input);
}

function problemWith(row, input) {
  if (row.type === 'int' || row.type === 'float') {
    if (String(input).trim() === '') return 'Enter a value, or reset it.';
    const number = Number(input);
    if (!Number.isFinite(number)) return 'Must be a number.';
    if (row.type === 'int' && !Number.isInteger(number)) return 'Must be a whole number.';
    if (row.min !== null && number < row.min) return `At least ${row.min}.`;
    if (row.max !== null && number > row.max) return `At most ${row.max}.`;
  }
  if (row.type === 'list') {
    const lines = fromInput(row, input);
    if (row.max_items && lines.length > row.max_items) return `At most ${row.max_items} lines.`;
    if (row.max_item_length && lines.some((line) => line.length > row.max_item_length)) {
      return `Lines may be at most ${row.max_item_length} characters.`;
    }
  }
  return null;
}

function same(row, a, b) {
  return JSON.stringify(fromInput(row, toInput(row, a))) === JSON.stringify(b);
}

function describe(row, value) {
  if (row.type === 'bool') return value ? 'on' : 'off';
  if (row.type === 'list') {
    const count = (value || []).length;
    return count === 0 ? 'none' : `${count} question${count === 1 ? '' : 's'}`;
  }
  if (row.type === 'version') return value || 'none';
  return row.unit ? `${value} ${row.unit}` : String(value);
}

function errorsByField(cause) {
  const rows = cause?.details?.errors;
  if (!Array.isArray(rows)) return null;
  return Object.fromEntries(rows.map((row) => [row.field, row.message]));
}

// ------------------------------------------------------------------- control
function Control({ row, input, onChange, disabled, versions }) {
  const id = `setting-${row.key}`;
  if (row.type === 'bool') {
    return (
      <Switch
        checked={Boolean(input)}
        onChange={onChange}
        disabled={disabled}
        label={input ? 'On' : 'Off'}
        title={row.label}
      />
    );
  }
  if (row.type === 'list') {
    return (
      <textarea
        id={id}
        aria-label={row.ariaLabel || row.label}
        className="textarea"
        rows={5}
        disabled={disabled}
        value={input}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  if (row.type === 'version') {
    return (
      <select
        id={id}
        aria-label={row.ariaLabel || row.label}
        className="select"
        disabled={disabled}
        value={input}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">None — the only ready version</option>
        {input && !versions.some((version) => version.id === input) && (
          <option value={input}>{input} (not found)</option>
        )}
        {versions.map((version) => (
          <option key={version.id} value={version.id} disabled={!version.is_ready}>
            {version.label || version.id}
            {version.is_ready ? '' : ' (not ready)'}
          </option>
        ))}
      </select>
    );
  }
  return (
    <span className="rs-row__number">
      <input
        id={id}
        aria-label={row.ariaLabel || row.label}
        className="input"
        type="number"
        inputMode={row.type === 'int' ? 'numeric' : 'decimal'}
        min={row.min ?? undefined}
        max={row.max ?? undefined}
        step={row.step ?? (row.type === 'int' ? 1 : 'any')}
        disabled={disabled}
        value={input}
        onChange={(event) => onChange(event.target.value)}
      />
      {row.unit && <span className="xs muted">{row.unit}</span>}
    </span>
  );
}

function Meta({ row }) {
  const parts = [`Default ${describe(row, row.default)}`];
  parts.push(row.env ? `from ${row.env}` : 'built in');
  if (row.min !== null && row.max !== null) parts.push(`${row.min}–${row.max}`);
  return (
    <div className="xs subtle">
      {parts.join(' · ')}
      {row.note && <> · {row.note}</>}
    </div>
  );
}

// ------------------------------------------------------- deployment section
function DeploymentRow({ row, entry, onChange, onReset, onUndo, canEdit, error, versions }) {
  const saved = row.deployment !== null;
  const current = saved ? row.deployment : row.default;
  const resetting = entry?.reset;
  const input = resetting ? toInput(row, row.default) : (entry?.input ?? toInput(row, current));

  return (
    <div className="rs-row">
      <div className="rs-row__text">
        <div className="rs-row__label">
          <label htmlFor={`setting-${row.key}`}>{row.label}</label>
          {saved && !resetting && <Badge tone="ok">saved</Badge>}
          {resetting && <Badge tone="warn">resets on save</Badge>}
          {entry && !resetting && <Badge tone="warn">changed</Badge>}
          {!row.active && <Badge title={APPLIES[row.applies]}>no effect here</Badge>}
        </div>
        <div className="xs muted">{row.description}</div>
        <Meta row={row} />
        {!row.active && <div className="xs subtle">{APPLIES[row.applies]}</div>}
      </div>
      <div className="rs-row__control">
        <Control
          row={row}
          input={input}
          onChange={onChange}
          disabled={!canEdit || resetting}
          versions={versions}
        />
        {error && <span className="field__error">{error}</span>}
        {canEdit && (saved || resetting) && (
          <span>
            {resetting ? (
              <Button size="sm" variant="ghost" onClick={onUndo}>
                Keep the saved value
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={onReset}>
                Reset to default
              </Button>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------ organization section
function OrganizationRow({ row, entry, onChange, canEdit, error, deploymentValue }) {
  const overriding = entry ? entry.override : row.organization !== null;
  const input = entry?.input ?? toInput(row, row.organization ?? deploymentValue);
  return (
    <div className="rs-row">
      <div className="rs-row__text">
        <div className="rs-row__label">
          <span>{row.label}</span>
          {row.organization !== null && !entry && <Badge tone="ok">overridden</Badge>}
          {entry && <Badge tone="warn">changed</Badge>}
        </div>
        <div className="xs muted">{row.description}</div>
        <div className="xs subtle">
          Deployment value: {describe(row, deploymentValue)}
          {overriding ? '' : ' — in force for this organization'}
        </div>
      </div>
      <div className="rs-row__control">
        <Switch
          checked={overriding}
          disabled={!canEdit}
          onChange={(on) => onChange({ override: on, input })}
          label="Override for this organization"
          title={`Override ${row.label.toLowerCase()} for this organization`}
        />
        {overriding && (
          <Control
            row={{ ...row, key: `org-${row.key}`, ariaLabel: `${row.label} for this organization` }}
            input={input}
            onChange={(next) => onChange({ override: true, input: next })}
            disabled={!canEdit}
            versions={[]}
          />
        )}
        {error && <span className="field__error">{error}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------- tab
export function GeneralTab() {
  const toast = useToast();
  const { isSuperadmin, organization } = useAuth();
  const { apply: applyRuntime } = useRuntimeSettings();

  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [versions, setVersions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState(organization?.id || '');

  const [deploymentDraft, setDeploymentDraft] = useState({});
  const [organizationDraft, setOrganizationDraft] = useState({});
  const [deploymentErrors, setDeploymentErrors] = useState({});
  const [organizationErrors, setOrganizationErrors] = useState({});
  const [saving, setSaving] = useState(null);

  const ownOrganization = !organizationId || organizationId === organization?.id;

  const show = useCallback(
    (next) => {
      setView(next);
      // The app's own copy (follow-ups, sidebar size…) follows the user's organization.
      if (!next.organization?.id || next.organization.id === organization?.id) {
        applyRuntime(next);
      }
    },
    [applyRuntime, organization?.id],
  );

  const refresh = useCallback(async () => {
    try {
      const next = await settingsApi.runtime(ownOrganization ? {} : { organizationId });
      show(next);
      setOrganizationDraft({});
      setOrganizationErrors({});
      setError(null);
    } catch (cause) {
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, [organizationId, ownOrganization, show]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    versionsApi
      .list()
      .then((list) => setVersions(Array.isArray(list) ? list : []))
      .catch(() => setVersions([]));
  }, []);

  useEffect(() => {
    if (!isSuperadmin) return;
    organizationsApi
      .list()
      .then((list) => setOrganizations(Array.isArray(list) ? list : []))
      .catch(() => setOrganizations([]));
  }, [isSuperadmin]);

  const rows = useMemo(() => view?.settings || [], [view]);
  const byKey = useMemo(() => Object.fromEntries(rows.map((row) => [row.key, row])), [rows]);
  const overridable = rows.filter((row) => row.organization_override);
  const canEditDeployment = Boolean(view?.permissions?.edit_deployment);
  const canEditOrganization = Boolean(view?.permissions?.edit_organization);

  // ------------------------------------------------------------ deployment
  const changeDeployment = (row, input) => {
    setDeploymentErrors((current) => ({ ...current, [row.key]: undefined }));
    setDeploymentDraft((current) => {
      const saved = row.deployment !== null ? row.deployment : row.default;
      const next = { ...current };
      if (same(row, saved, fromInput(row, input)) && !current[row.key]?.reset) {
        delete next[row.key];
      } else {
        next[row.key] = { input };
      }
      return next;
    });
  };

  const resetDeployment = (row) =>
    setDeploymentDraft((current) => ({ ...current, [row.key]: { reset: true } }));

  const undoReset = (row) =>
    setDeploymentDraft((current) => {
      const next = { ...current };
      delete next[row.key];
      return next;
    });

  const deploymentProblems = useMemo(() => {
    const found = {};
    for (const [key, entry] of Object.entries(deploymentDraft)) {
      const row = byKey[key];
      if (row && !entry.reset) {
        const problem = problemWith(row, entry.input);
        if (problem) found[key] = problem;
      }
    }
    return found;
  }, [byKey, deploymentDraft]);

  const saveDeployment = async () => {
    const values = {};
    for (const [key, entry] of Object.entries(deploymentDraft)) {
      values[key] = entry.reset ? null : fromInput(byKey[key], entry.input);
    }
    setSaving('deployment');
    try {
      const next = await settingsApi.updateRuntime(values);
      show(next);
      setDeploymentDraft({});
      setDeploymentErrors({});
      toast.success(
        'Settings saved',
        'They apply to the next request, and to every API process within a few seconds.',
      );
    } catch (cause) {
      const fields = errorsByField(cause);
      if (fields) setDeploymentErrors(fields);
      else toast.error(cause, { title: 'Could not save the settings' });
    } finally {
      setSaving(null);
    }
  };

  // ---------------------------------------------------------- organization
  const deploymentValue = (row) => (row.deployment !== null ? row.deployment : row.default);

  const changeOrganization = (row, entry) => {
    setOrganizationErrors((current) => ({ ...current, [row.key]: undefined }));
    setOrganizationDraft((current) => {
      const next = { ...current };
      const wasOverriding = row.organization !== null;
      const unchanged = entry.override
        ? wasOverriding && same(row, row.organization, fromInput(row, entry.input))
        : !wasOverriding;
      if (unchanged) delete next[row.key];
      else next[row.key] = entry;
      return next;
    });
  };

  const organizationProblems = useMemo(() => {
    const found = {};
    for (const [key, entry] of Object.entries(organizationDraft)) {
      const row = byKey[key];
      if (row && entry.override) {
        const problem = problemWith(row, entry.input);
        if (problem) found[key] = problem;
      }
    }
    return found;
  }, [byKey, organizationDraft]);

  const saveOrganization = async () => {
    const target = view.organization.id;
    const values = {};
    for (const [key, entry] of Object.entries(organizationDraft)) {
      values[key] = entry.override ? fromInput(byKey[key], entry.input) : null;
    }
    setSaving('organization');
    try {
      const next = await settingsApi.updateOrganizationRuntime(target, values);
      show(next);
      setOrganizationDraft({});
      setOrganizationErrors({});
      toast.success(`Overrides saved for ${next.organization?.name || 'the organization'}`);
    } catch (cause) {
      const fields = errorsByField(cause);
      if (fields) setOrganizationErrors(fields);
      else toast.error(cause, { title: 'Could not save the overrides' });
    } finally {
      setSaving(null);
    }
  };

  if (loading && !view) return <Loading label="Reading the settings…" large />;
  if (error && !view) return <ErrorState error={error} onRetry={refresh} />;

  const deploymentChanges = Object.keys(deploymentDraft).length;
  const organizationChanges = Object.keys(organizationDraft).length;
  const deploymentBlocked = Object.keys(deploymentProblems).length > 0;
  const organizationBlocked = Object.keys(organizationProblems).length > 0;

  return (
    <div className="col" style={{ gap: 16, maxWidth: 980 }}>
      <section className="card card--pad">
        <div className="row" style={{ marginBottom: 6 }}>
          <div className="section-label">How these settings work</div>
          <div className="grow" />
          {!canEditDeployment && (
            <span className="xs muted">Read-only: a superadmin changes deployment values.</span>
          )}
        </div>
        <div className="small muted">
          Each setting starts at its .env value (shown as Default). A value saved here replaces it
          for the whole deployment without a restart. An organization&apos;s administrator can
          override the chat history, temperature and follow-up questions for their own organization.
          Reset goes back one layer.
        </div>
        <div className="xs subtle" style={{ marginTop: 8 }}>
          {view.deployment?.updated_by
            ? `Deployment values last changed by ${view.deployment.updated_by} ${relativeTime(
                view.deployment.updated_at,
              )}`
            : 'No deployment values saved yet — everything is at its default.'}
        </div>
      </section>

      {(view.warnings || []).map((warning) => (
        <section key={warning.code} className="card card--pad rs-warning" role="alert">
          <div className="section-label" style={{ marginBottom: 6 }}>
            Check this combination
          </div>
          <div className="small">{warning.message}</div>
        </section>
      ))}

      <section className="card card--pad">
        <div className="row" style={{ marginBottom: 6 }}>
          <div className="section-label">Overrides for {view.organization?.name}</div>
          <div className="grow" />
          {isSuperadmin && organizations.length > 1 && (
            <div style={{ minWidth: 220 }}>
              <Select
                aria-label="Organization"
                value={view.organization?.id || ''}
                onChange={(event) => setOrganizationId(event.target.value)}
                options={organizations.map((candidate) => ({
                  value: candidate.id,
                  label: candidate.name,
                }))}
              />
            </div>
          )}
        </div>
        <div className="xs subtle">
          {view.organization?.updated_by
            ? `Last changed by ${view.organization.updated_by} ${relativeTime(
                view.organization.updated_at,
              )}`
            : 'No overrides: the deployment values apply.'}
          {!canEditOrganization && ' Read-only: the organization’s administrator changes these.'}
        </div>
        {overridable.map((row) => (
          <OrganizationRow
            key={row.key}
            row={row}
            entry={organizationDraft[row.key]}
            onChange={(entry) => changeOrganization(row, entry)}
            canEdit={canEditOrganization}
            error={organizationErrors[row.key] || organizationProblems[row.key]}
            deploymentValue={deploymentValue(row)}
          />
        ))}
        {canEditOrganization && (
          <div className="row" style={{ marginTop: 12 }}>
            <div className="grow" />
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setOrganizationDraft({});
                setOrganizationErrors({});
              }}
              disabled={!organizationChanges}
            >
              Discard
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={saveOrganization}
              loading={saving === 'organization'}
              disabled={!organizationChanges || organizationBlocked}
            >
              Save overrides
            </Button>
          </div>
        )}
      </section>

      {(view.groups || []).map((group) => (
        <section key={group.id} className="card card--pad">
          <div className="section-label" style={{ marginBottom: 4 }}>
            {group.label}
          </div>
          {rows
            .filter((row) => row.group === group.id)
            .map((row) => (
              <DeploymentRow
                key={row.key}
                row={row}
                entry={deploymentDraft[row.key]}
                onChange={(input) => changeDeployment(row, input)}
                onReset={() => resetDeployment(row)}
                onUndo={() => undoReset(row)}
                canEdit={canEditDeployment}
                error={deploymentErrors[row.key] || deploymentProblems[row.key]}
                versions={versions}
              />
            ))}
        </section>
      ))}

      {canEditDeployment && (
        <div className="card card--pad rs-savebar">
          <span className="small muted">
            {deploymentChanges
              ? `${deploymentChanges} unsaved change${deploymentChanges === 1 ? '' : 's'} to the deployment values`
              : 'No unsaved changes'}
          </span>
          <div className="grow" />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setDeploymentDraft({});
              setDeploymentErrors({});
            }}
            disabled={!deploymentChanges}
          >
            Discard
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={saveDeployment}
            loading={saving === 'deployment'}
            disabled={!deploymentChanges || deploymentBlocked}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
}
